import { ref } from "vue";
import { useAuthenticatedFetch } from "../auth/useAuthenticatedFetch";

import {
  Trainer,
  TrainerRequest,
  TrainerSearchParams,
  TrainersToPayParams,
  SearchTrainerAvailabilityParams,
  ReservedSlotsParams,
  ReservedSlotsResponseItem,
} from "@/interfaces/trainer";
import { trainers, trainer, clubUUID, paginationLinks } from "@/shared/state";

const { authenticatedFetch } = useAuthenticatedFetch();
const API_URL = process.env.VUE_APP_ROOT_API;
export function useTrainer() {
  const errorTrainer = ref<boolean | false>(false);
  const isRequestTrainer = ref<boolean | false>(false);
  const getTrainers = async () => {
    try {
      isRequestTrainer.value = true;
      errorTrainer.value = false;
      const response = await authenticatedFetch(
        `${API_URL}trainers/${clubUUID.value}`,
        {
          method: "GET",
        },
        false,
        "",
        false
      );
      trainers.data = response?.data || [];
      isRequestTrainer.value = false;
    } catch (err: any) {
      errorTrainer.value = true;
      isRequestTrainer.value = false;
    }
  };

  const getTrainer = async (id: string) => {
    try {
      isRequestTrainer.value = true;
      errorTrainer.value = false;
      const response = await authenticatedFetch(
        `${API_URL}trainers/${id}/${clubUUID.value}`,
        {
          method: "GET",
        },
        false,
        "",
        false
      );
      trainer.data = response?.data || [];
      isRequestTrainer.value = false;
    } catch (err: any) {
      errorTrainer.value = true;
      isRequestTrainer.value = false;
    }
  };
  const fetchTrainers = async (body: TrainerSearchParams) => {
    try {
      isRequestTrainer.value = true;
      errorTrainer.value = false;
      trainers.data = [];
      paginationLinks.value = 0;
      const response = await authenticatedFetch(
        `${API_URL}trainers/search`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        false,
        "",
        false
      );
      if (response) {
        if (body?.type === "paginate") {
          trainers.data = response?.data?.data || [];
          paginationLinks.value = response?.data;
          delete paginationLinks.value.data;
        } else if (body?.type === "all") {
          trainers.data = response?.data || [];
        } else {
          // fiche_trainers.data = response?.data || {}
        }
        isRequestTrainer.value = false;
      }
    } catch (err: any) {
      errorTrainer.value = true;
      isRequestTrainer.value = false;
    }
  };

  const fetchTrainersToPay = async (body: TrainersToPayParams) => {
    try {
      isRequestTrainer.value = true;
      errorTrainer.value = false;
      trainers.data = [];
      paginationLinks.value = 0;
      const response = await authenticatedFetch(
        `${API_URL}trainers/to-pay`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        false,
        "",
        false
      );
      if (response) {
        trainers.data = response?.data || [];
        isRequestTrainer.value = false;
      }
    } catch (err: any) {
      errorTrainer.value = true;
      isRequestTrainer.value = false;
    }
  };

  const addTrainer = async (body: TrainerRequest) => {
    try {
      isRequestTrainer.value = true;
      errorTrainer.value = false;
      const response = await authenticatedFetch(
        `${API_URL}trainers/store/${clubUUID.value}`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        true,
        "",
        true
      );
      if (response) {
        const TrainerAdded: Trainer = response?.data;
        trainers?.data?.unshift(TrainerAdded);
      }
      isRequestTrainer.value = false;
    } catch (err: any) {
      errorTrainer.value = true;
      isRequestTrainer.value = false;
    }
  };

  const editTrainer = async (body: TrainerRequest) => {
    try {
      isRequestTrainer.value = true;
      errorTrainer.value = false;
      const response = await authenticatedFetch(
        `${API_URL}trainers/edit/${body?.id}/${clubUUID.value}`,
        {
          method: "PUT",
          body: JSON.stringify(body),
        },
        true,
        "",
        true
      );
      if (response) {
        const TrainerEdited: Trainer = response?.data;
        const index = trainers?.data?.findIndex(
          (trainer) => trainer?.id === TrainerEdited?.id
        );
        if (index !== -1) {
          trainers.data[index] = TrainerEdited;
        }
      }
      isRequestTrainer.value = false;
    } catch (err: any) {
      errorTrainer.value = true;
      isRequestTrainer.value = false;
    }
  };

  const deleteTrainer = async (id: number) => {
    try {
      isRequestTrainer.value = true;
      errorTrainer.value = false;
      const response = await authenticatedFetch(
        `${API_URL}trainers/delete/${id}/${clubUUID.value}`,
        {
          method: "DELETE",
        },
        true,
        "",
        true
      );
      const index = trainers?.data?.findIndex((trainer) => trainer?.id === id);
      if (index !== -1) {
        trainers.data.splice(index, 1);
      }
      isRequestTrainer.value = false;
    } catch (err: any) {
      errorTrainer.value = true;
      isRequestTrainer.value = false;
    }
  };
  const fetchTrainerAvailabilty = async (
    body: SearchTrainerAvailabilityParams
  ) => {
    try {
      isRequestTrainer.value = true;
      errorTrainer.value = false;
      const response = await authenticatedFetch(
        `${API_URL}private-sessions/trainer/availability/${clubUUID.value}`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        true,
        "",
        true
      );
      if (response) {
        isRequestTrainer.value = false;
        return response?.data;
      }
    } catch (err: any) {
      errorTrainer.value = true;
      isRequestTrainer.value = false;
    }
  };

  const fetchReservedSlots = async (
    body: ReservedSlotsParams
  ): Promise<ReservedSlotsResponseItem[] | null> => {
    try {
      isRequestTrainer.value = true;
      errorTrainer.value = false;
      const payload = {
        trainer_id: body.trainer_id,
        session_date: body.session_date,
        clubUUID: body.clubUUID ?? clubUUID.value,
      };
      const response = await authenticatedFetch(
        `${API_URL}trainers/reserved-slots`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
        false,
        "",
        false
      );
      isRequestTrainer.value = false;
      return response ?? null;
    } catch (err: any) {
      isRequestTrainer.value = false;
      errorTrainer.value = true;
      return null;
    }
  };

  return {
    fetchTrainerAvailabilty,
    fetchReservedSlots,
    getTrainer,
    getTrainers,
    addTrainer,
    editTrainer,
    deleteTrainer,
    isRequestTrainer,
    errorTrainer,
    fetchTrainers,
    fetchTrainersToPay,
  };
}
